import React, { useMemo, useState } from 'react';
import { EventCard } from './EventCard';
import { CalendarIcon, BookmarkIcon, Share2Icon } from 'lucide-react';
export function HomeScreen({
  events,
  onEditEvent,
  onDeleteEvent,
  onToggleFavorite,
  onShareList
}) {
  const [timeFilter, setTimeFilter] = useState('thisWeekend');
  const timeFilters = [{
    id: 'tonight',
    label: 'Tonight'
  }, {
    id: 'thisWeekend',
    label: 'This Weekend'
  }, {
    id: 'nextWeek',
    label: 'Next Week'
  }, {
    id: 'thisMonth',
    label: 'This Month'
  }, {
    id: 'recentlySaved',
    label: 'Recently Saved',
    icon: <BookmarkIcon className="w-3 h-3 mr-1" data-id="element-308" />
  }];
  // Helper function to parse date string to avoid timezone issues
  const parseDate = dateString => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  };
  // Helper to get start of day
  const getStartOfDay = date => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };
  // Helper to get start of week (Monday)
  const getStartOfWeek = date => {
    const newDate = new Date(date);
    const day = newDate.getDay();
    const diff = newDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    newDate.setDate(diff);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };
  // Helper to get end of week (Sunday)
  const getEndOfWeek = date => {
    const newDate = getStartOfWeek(date);
    newDate.setDate(newDate.getDate() + 6);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };
  // Helper to get start of month
  const getStartOfMonth = date => {
    const newDate = new Date(date);
    newDate.setDate(1);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };
  // Helper to get end of month
  const getEndOfMonth = date => {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + 1);
    newDate.setDate(0);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };
  // Get recently saved events (within last 5 days)
  const getRecentlySavedEvents = () => {
    if (!events.length) return [];
    // Calculate date from 5 days ago
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
    return [...events].filter(event => {
      // Filter events created within the last 5 days
      const creationDate = new Date(event.id); // Using id as timestamp if dateCreated isn't available
      return creationDate >= fiveDaysAgo;
    }).sort((a, b) => b.id - a.id) // Sort by newest first using id as timestamp
    .slice(0, 5); // Limit to 5 most recent events
  };
  const filteredEvents = useMemo(() => {
    if (!events.length) return [];
    // Handle the "Recently Saved" filter separately
    if (timeFilter === 'recentlySaved') {
      return getRecentlySavedEvents();
    }
    // Get current date info
    const now = new Date();
    const today = getStartOfDay(now);
    // Calculate this weekend (Friday - Sunday)
    const thisWeekend = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate days until Friday
    const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 5 + 7 - dayOfWeek;
    thisWeekend.setDate(today.getDate() + daysUntilFriday);
    const weekendStart = getStartOfDay(thisWeekend); // Friday
    const weekendEnd = new Date(weekendStart);
    weekendEnd.setDate(weekendStart.getDate() + 2); // Sunday
    weekendEnd.setHours(23, 59, 59, 999);
    // Calculate next week (Monday - Sunday of next week)
    const currentWeekEnd = getEndOfWeek(today);
    const nextWeekStart = new Date(currentWeekEnd);
    nextWeekStart.setDate(currentWeekEnd.getDate() + 1);
    nextWeekStart.setHours(0, 0, 0, 0);
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
    nextWeekEnd.setHours(23, 59, 59, 999);
    // Calculate this month (all days in current calendar month)
    const thisMonthStart = getStartOfMonth(today);
    const thisMonthEnd = getEndOfMonth(today);
    return events.filter(event => {
      if (!event.date) return false;
      const eventDate = parseDate(event.date);
      if (!eventDate) return false;
      switch (timeFilter) {
        case 'tonight':
          // Today only
          return eventDate.toDateString() === today.toDateString();
        case 'thisWeekend':
          // Friday, Saturday, Sunday of this week
          return eventDate >= weekendStart && eventDate <= weekendEnd;
        case 'nextWeek':
          // Monday through Sunday of next week
          return eventDate >= nextWeekStart && eventDate <= nextWeekEnd;
        case 'thisMonth':
          // All days in current calendar month
          return eventDate >= thisMonthStart && eventDate <= thisMonthEnd;
        default:
          return true;
      }
    }).sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateA - dateB;
    });
  }, [events, timeFilter]);
  return <div className="space-y-6" data-id="element-309">
      {/* Time-First Dashboard */}
      <div data-id="element-310">
        <div className="flex items-center justify-between mb-4" data-id="element-311">
          <h2 className="text-lg font-medium flex items-center" data-id="element-312">
            <CalendarIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-313" />
            {timeFilter === 'recentlySaved' ? 'Recently Saved' : 'Upcoming Events'}
          </h2>
          {onShareList && <button onClick={onShareList} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Share list" data-id="element-314">
              <Share2Icon className="w-5 h-5 text-gray-600" data-id="element-315" />
            </button>}
        </div>
        {/* Quick Filters */}
        <div className="flex space-x-2 overflow-x-auto pb-2 mb-4" data-id="element-316">
          {timeFilters.map(filter => <button key={filter.id} className={`px-3 py-1 text-sm rounded-full whitespace-nowrap flex items-center ${timeFilter === filter.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setTimeFilter(filter.id)} data-id="element-317">
              {filter.icon && <span className={timeFilter === filter.id ? 'text-white' : ''} data-id="element-318">
                  {filter.icon}
                </span>}
              {filter.label}
            </button>)}
        </div>
        {/* Description for Recently Saved */}
        {timeFilter === 'recentlySaved' && <p className="text-sm text-gray-500 mb-4" data-id="element-319">
            Events saved within the last 5 days
          </p>}
        {/* Filtered Events */}
        <div className="space-y-4" data-id="element-320">
          {filteredEvents.length === 0 ? <div className="text-center py-6 bg-white rounded-lg shadow" data-id="element-321">
              <p className="text-gray-500" data-id="element-322">
                {timeFilter === 'recentlySaved' ? 'No recently saved events' : 'No events found for this time period'}
              </p>
              <p className="text-sm text-gray-400 mt-1" data-id="element-323">
                {timeFilter === 'recentlySaved' ? "You haven't saved any events in the last 5 days" : 'Try another filter or add some events'}
              </p>
            </div> : filteredEvents.map(event => <EventCard key={`${timeFilter}-${event.id}`} event={event} onEditEvent={onEditEvent} onDeleteEvent={onDeleteEvent} onToggleFavorite={onToggleFavorite} data-id="element-324" />)}
        </div>
      </div>
    </div>;
}